import MenuIcon from '@mui/icons-material/Menu';
import {
  Box,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useMemo } from 'react';
import { useLocation, useOutletContext } from 'react-router';

import { isPathActive } from 'common/src/routes/helpers';

import usePageTitle from 'portal/src/navigation/usePageTitle';

import OrganizationSwitcherLegacy from './OrganizationSwitcherLegacy';
import TabsLegacy from './TabsLegacy';

import useAuth from '../../auth/useAuth';
import { IHeaderTab, LayoutContext } from '../LayoutPreference/layoutTypes';
import UserProfilePopupLegacy from '../menu/UserProfilePopupLegacy';

/** @deprecated Temporary dual-layout rollout legacy header. Remove when legacy layout is retired. */
const HeaderLegacy = ({
  title,
  tabs = [],
}: {
  title?: string;
  tabs?: IHeaderTab[] | undefined;
}) => {
  const {
    featureChecker,
    isAuthenticated,
    hasAdminAccess,
    hasClientSystemAdminAccess,
    hasOrgSwitchingAccess,
    hasReadOnlyAccess,
    hasSystemAdminAccess,
  } = useAuth();
  const { breakpoints } = useTheme();
  const { setOpenMobile } = useOutletContext<LayoutContext>();
  const isMobile = useMediaQuery(breakpoints.down('lg'));
  const { pathname } = useLocation();

  const isAuthed = isAuthenticated();

  const tabsToRender = useMemo(() => {
    return tabs.filter((t) => {
      return (
        !t.shouldRender ||
        t.shouldRender({
          featureChecker,
          hasAdminAccess,
          hasClientSystemAdminAccess,
          hasReadOnlyAccess,
          hasSystemAdminAccess,
        })
      );
    });
  }, [
    featureChecker,
    hasAdminAccess,
    hasClientSystemAdminAccess,
    hasReadOnlyAccess,
    hasSystemAdminAccess,
    tabs,
  ]);

  // Find the tab matching the root of the current path
  let activeTab = tabsToRender.findIndex((t) => isPathActive(pathname, t.path));

  if (activeTab < 0) {
    activeTab = 0;
  }

  const currentTab = tabsToRender[activeTab];
  const subTitle = currentTab?.subTitle ?? '';
  const documentTitle = currentTab?.name;

  usePageTitle(documentTitle);

  return (
    <Box sx={{ backgroundColor: 'background.paper', boxShadow: 1, p: 1 }}>
      <Stack direction="row" justifyContent="space-between">
        <Stack alignItems="center" direction="row">
          {/* Mobile Hamburger Icon */}
          {isAuthed && isMobile && (
            <IconButton
              aria-label="menu"
              onClick={() => setOpenMobile(true)}
              sx={{ height: 'auto' }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Stack ml={2}>
            <Typography
              textAlign="left"
              sx={{
                typography: { md: 'h4', xs: 'body1' },
                color: 'primary.main',
              }}
              variant="h1"
            >
              {title}
            </Typography>
            {!useMediaQuery(breakpoints.down('md')) && (
              <Typography variant="subtitle1">{subTitle}</Typography>
            )}
          </Stack>
        </Stack>
        <Box>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap={1}
            mr={2}
          >
            {isAuthed && hasOrgSwitchingAccess() && (
              <OrganizationSwitcherLegacy />
            )}
            <UserProfilePopupLegacy />
          </Box>
        </Box>
      </Stack>
      <TabsLegacy value={activeTab} tabs={tabsToRender} />
    </Box>
  );
};

export default HeaderLegacy;
